import type { ServerResponse } from 'http';

import type { Moment } from 'moment';
import { createAction, createAsyncAction, getType } from 'typesafe-actions';

import type {
  AccountDeactivation,
  AccountOwnersWriteData,
} from '@evenfinancial/finance-client';

import type { AccountSelfService, TempAccount } from '@portal/api-models';
import type { PortalError } from '@portal/common';

import type { IsomorphicRequestPayload } from '../types';

import { AccountActionType } from './actionTypes';
import type { PayoutSettings } from './types';

export interface BillingItemForm {
  effectiveAt?: Moment;
  netD?: string;
  termUnit?: string;
}

export interface AccountCreateForm {
  accountOwners: AccountOwnersWriteData;
  billingName?: string;
  displayName?: string;

  name?: string;
  payable: BillingItemForm[];
  receivable: BillingItemForm[];
}

export const accountRequestAction = createAsyncAction(
  AccountActionType.GET_ALL_REQUEST,
  AccountActionType.GET_ALL_SUCCESS,
  AccountActionType.GET_ALL_FAILURE
)<IsomorphicRequestPayload, TempAccount[], Error>();

export const accountCreateRequestAction = createAsyncAction(
  AccountActionType.CREATE_REQUEST_CLIENT,
  AccountActionType.CREATE_SUCCESS_CLIENT,
  AccountActionType.CREATE_FAILURE_CLIENT
)<AccountCreateForm, TempAccount, PortalError>();

export const accountUpdateRequestAction = createAsyncAction(
  AccountActionType.UPDATE_REQUEST_CLIENT,
  AccountActionType.UPDATE_SUCCESS_CLIENT,
  AccountActionType.UPDATE_FAILURE_CLIENT
)<IsomorphicRequestPayload & Partial<TempAccount>, TempAccount, Error>();

export const accountUpdatePayoutSettingsRequestAction = createAsyncAction(
  AccountActionType.UPDATE_PAYOUT_SETTINGS_REQUEST_CLIENT,
  AccountActionType.UPDATE_PAYOUT_SETTINGS_SUCCESS_CLIENT,
  AccountActionType.UPDATE_PAYOUT_SETTINGS_FAILURE_CLIENT
)<IsomorphicRequestPayload & PayoutSettings, TempAccount, Error>();

export interface AccountByUuidRequestPayload extends IsomorphicRequestPayload {
  res?: ServerResponse;
  uuid: string;
}

export const accountByUuidAction = createAsyncAction(
  AccountActionType.BY_UUID_REQUEST,
  AccountActionType.BY_UUID_SUCCESS,
  AccountActionType.BY_UUID_FAILURE
)<AccountByUuidRequestPayload, TempAccount, Error>();

export const accountDeactivationRequestAction = createAsyncAction(
  AccountActionType.GET_ALL_DEACTIVATION_REQUEST,
  AccountActionType.GET_ALL_DEACTIVATION_SUCCESS,
  AccountActionType.GET_ALL_DEACTIVATION_FAILURE
)<IsomorphicRequestPayload, AccountDeactivation[], Error>();

export const createAccountDeactivationsAction = createAsyncAction(
  AccountActionType.CREATE_ACCOUNT_DEACTIVATIONS_REQUEST,
  AccountActionType.CREATE_ACCOUNT_DEACTIVATIONS_SUCCESS,
  AccountActionType.CREATE_ACCOUNT_DEACTIVATIONS_FAILURE
)<{ accountIds: number[] }, AccountDeactivation[], Error>();

export const deleteAccountDeactivationAction = createAsyncAction(
  AccountActionType.DELETE_ACCOUNT_DEACTIVATIONS_REQUEST,
  AccountActionType.DELETE_ACCOUNT_DEACTIVATIONS_SUCCESS,
  AccountActionType.DELETE_ACCOUNT_DEACTIVATIONS_FAILURE
)<{ deactivationIds: number[] }, AccountDeactivation[], Error>();

export const accountsSelfServiceRequestAction = createAsyncAction(
  AccountActionType.GET_ALL_SELF_SERVICE_REQUEST,
  AccountActionType.GET_ALL_SELF_SERVICE_SUCCESS,
  AccountActionType.GET_ALL_SELF_SERVICE_FAILURE
)<IsomorphicRequestPayload, AccountSelfService[], Error>();

export const accountsSave = createAction(getType(accountRequestAction.success))<
  TempAccount[]
>();

export const toggleDuplicateAccount = createAction(
  AccountActionType.TOGGLE_DUPLICATE
)<string>();

export const toggleCreateAccount = createAction(
  AccountActionType.TOGGLE_CREATE
)();
